import { makeStyles } from "@material-ui/core";
import carecentreTokens from "../../design-system/carecentre/tokens";

const { color, font, radius, space, type } = carecentreTokens;

export const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
    maxWidth: 1040,
    margin: "0 auto",
    padding: `${space[6]} ${space[4]} ${space[10]}`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    animation: "$rise 420ms cubic-bezier(0.16, 1, 0.3, 1)",
    "@media (max-width: 640px)": {
      padding: `${space[4]} ${space[3]} ${space[6]}`,
    },
  },
  "@keyframes rise": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  panel: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 300px",
    gap: space[6],
    alignItems: "start",
    background: color.bgCard,
    border: `1px solid ${color.line}`,
    borderRadius: 20,
    padding: `${space[8]} ${space[8]}`,
    boxShadow:
      "0 12px 32px -4px rgba(21, 25, 31, 0.07), 0 2px 6px rgba(21, 25, 31, 0.03)",
    boxSizing: "border-box",
    "@media (max-width: 840px)": {
      gridTemplateColumns: "1fr",
      gap: space[5],
      padding: `${space[6]} ${space[5]}`,
    },
    "@media (max-width: 560px)": {
      padding: `${space[5]} ${space[4]}`,
      borderRadius: radius.lg,
    },
  },
  copy: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
    gap: space[3],
  },
  headerRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: space[2],
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    ...type.label,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: color.primaryDeep,
    background: color.primarySoft,
    margin: 0,
    padding: "5px 12px",
    borderRadius: radius.full,
  },
  openedChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    ...type.caption,
    color: color.textBody,
    background: color.bgBone,
    border: `1px solid ${color.line}`,
    padding: "4px 10px",
    borderRadius: radius.full,
    "& strong": {
      color: color.textInk,
      fontWeight: 700,
    },
  },
  display: {
    margin: 0,
    fontFamily: font.display,
    fontSize: 34,
    lineHeight: "40px",
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: color.textInk,
    textWrap: "balance",
    "@media (max-width: 560px)": {
      fontSize: 28,
      lineHeight: "34px",
    },
  },
  displayName: {
    color: color.primary,
  },
  descriptionText: {
    ...type.body,
    fontSize: 15,
    lineHeight: "24px",
    color: color.textBody,
    margin: 0,
    maxWidth: "60ch",
  },
  planHtml: {
    ...type.body,
    fontSize: 15,
    lineHeight: "24px",
    color: color.textBody,
    margin: 0,
    width: "100%",
    maxWidth: "65ch",
    "& p": {
      margin: "0 0 10px",
    },
    "& p:last-child": {
      marginBottom: 0,
    },
    "& strong, & b": {
      fontWeight: 700,
      color: color.textInk,
    },
    "& ul, & ol": {
      margin: "8px 0 12px",
      padding: "0 0 0 20px",
    },
    "& ul:last-child, & ol:last-child": {
      marginBottom: 0,
    },
    "& li": {
      margin: "0 0 6px",
      padding: 0,
      color: color.textBody,
      lineHeight: "22px",
    },
    "& li:last-child": {
      marginBottom: 0,
    },
  },
  lead: {
    ...type.body,
    fontSize: 16,
    lineHeight: "24px",
    color: color.textInk,
    fontWeight: 600,
    margin: 0,
    maxWidth: "65ch",
  },
  sub: {
    ...type.bodySmall,
    color: color.textMuted,
    margin: 0,
  },
  featuresSection: {
    marginTop: space[3],
    width: "100%",
  },
  featuresList: {
    margin: "8px 0 0",
    padding: "0 0 0 20px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  featureItem: {
    ...type.body,
    fontSize: 14.5,
    lineHeight: "22px",
    color: color.textBody,
  },
  aside: {
    alignSelf: "start",
    position: "sticky",
    top: 24,
    display: "flex",
    flexDirection: "column",
    gap: space[5],
    padding: `${space[5]} ${space[4]}`,
    background: color.bgBone,
    border: `1px solid ${color.lineSoft}`,
    borderRadius: radius.lg,
    boxSizing: "border-box",
    "@media (max-width: 840px)": {
      position: "static",
      padding: space[4],
    },
  },
  asideTop: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    width: "100%",
  },
  asideHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    minHeight: 22,
  },
  priceLine: {
    margin: 0,
    display: "inline-flex",
    alignItems: "baseline",
    gap: 6,
    "& strong": {
      fontFamily: font.display,
      fontSize: 34,
      lineHeight: "38px",
      fontWeight: 800,
      letterSpacing: "-0.03em",
      color: color.textInk,
      fontVariantNumeric: "tabular-nums",
    },
  },
  priceFrom: {
    ...type.label,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: color.textMuted,
  },
  priceUnit: {
    ...type.caption,
    fontSize: 13,
    color: color.textMuted,
    whiteSpace: "nowrap",
  },
  trialBadge: {
    display: "inline-flex",
    alignItems: "center",
    ...type.label,
    fontSize: 11,
    fontWeight: 700,
    color: color.success,
    background: color.successSoft,
    padding: "3px 8px",
    borderRadius: radius.full,
    marginLeft: "auto",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  asideActions: {
    display: "flex",
    flexDirection: "column",
    gap: space[2],
  },
  asideBtn: {
    width: "100% !important",
  },
  allPlansLink: {
    border: "none",
    background: "none",
    padding: "4px 0 0",
    margin: 0,
    cursor: "pointer",
    fontFamily: font.body,
    fontSize: 12,
    fontWeight: 600,
    color: color.primary,
    textAlign: "center",
    width: "100%",
    transition: "color 120ms ease",
    "&:hover": {
      color: color.primaryDeep,
      textDecoration: "underline",
    },
  },

  sectionLabel: {
    ...type.label,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: color.textQuiet,
    margin: `0 0 ${space[2]}`,
  },
  sectionTitle: {
    fontFamily: font.body,
    fontSize: 15,
    lineHeight: "20px",
    fontWeight: 700,
    color: color.textInk,
    margin: `0 0 ${space[1]}`,
  },
  sectionSub: {
    ...type.caption,
    color: color.textMuted,
    margin: `0 0 ${space[3]}`,
  },

  ctaRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space[4],
    "@media (max-width: 560px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  ctaButtons: {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    gap: space[2],
    "@media (max-width: 560px)": {
      flexDirection: "column",
      width: "100%",
    },
  },
  demoCard: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space[4],
    "@media (max-width: 560px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  demoHint: {
    ...type.bodySmall,
    color: color.textBody,
    margin: 0,
    maxWidth: "32ch",
  },
  demoCta: {
    flexShrink: 0,
    "@media (max-width: 560px)": {
      width: "100%",
    },
  },

  // Shared step body — flush with card left edge (no nested inset).
  stepBody: {
    display: "flex",
    flexDirection: "column",
    gap: space[3],
    width: "100%",
  },
  fieldStack: {
    display: "flex",
    flexDirection: "column",
    gap: space[3],
    width: "100%",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: space[3],
    width: "100%",
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
    },
  },
  formActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: space[3],
    marginTop: space[3],
  },
  backLink: {
    border: "none",
    background: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    fontFamily: font.body,
    fontSize: 13,
    fontWeight: 500,
    color: color.textMuted,
    lineHeight: 1,
    "&:hover": {
      color: color.textInk,
    },
    "&:disabled": {
      opacity: 0.4,
      cursor: "default",
    },
  },

  slotWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: space[2],
    "@media (max-width: 420px)": {
      gridTemplateColumns: "1fr",
    },
  },
  slotCard: {
    padding: `${space[2]} ${space[2]}`,
    backgroundColor: color.bgCard,
    borderRadius: radius.sm,
    cursor: "pointer",
    border: `1px solid ${color.line}`,
    color: color.textBody,
    fontFamily: font.body,
    fontSize: 12,
    fontWeight: 500,
    textAlign: "center",
    lineHeight: 1.3,
    transition: "border-color 120ms ease, background 120ms ease",
    "&:hover": {
      borderColor: color.lineStrong,
      background: color.bgBone,
    },
  },
  selected: {
    border: `1.5px solid ${color.primary}`,
    background: color.primaryFaint,
    color: color.primaryDeep,
    fontWeight: 700,
    "&:hover": {
      borderColor: color.primary,
      background: color.primaryFaint,
    },
  },
}));
